import type { FileInfo, WorkspaceRoot } from '@/api/file';
import * as fileApi from '@/api/file';
import * as authApi from '@/api/auth';
import * as shareApi from '@/api/share';
import {
    FileExcelOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    CaretRightOutlined as FolderClose,
    CaretDownOutlined as FolderOpen,
    LoadingOutlined
} from '@ant-design/icons';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { getAntdApp } from '@/utils/antdApp';
import pathbe from 'path-browserify-esm';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { FileEditorMapService } from './fileEditorMapService';
import { fileEventQueue, FileEventType } from './fileEvent';
import { fileService, isExcelFile, isLogFile, isWordFile } from './filesService';
import { enableMapSet } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { Database as editorDb } from '@/utils/persistence';
import { validateLegalString } from './illegalStringValidator';
import { websocketService } from '@/utils/websocketService';
import i18n from '../../i18n';
export type FileInfoExtended = FileInfo & {
    icon?: React.ReactNode;
}

enableMapSet();

export enum FileType {
    TEXT = "text",
    GAUSSIAN = "gaussian",
    IMAGE = "image",
    PDF = "pdf",
    BLOB = "blob",
}

export enum FileExtension {
    INP = ".inp",
    PY = ".py",
    LOG = ".log",
    XYZ = ".xyz",
    PDB = ".pdb",
    PQR = ".pqr",
    MOL2 = ".mol2",
    COM = ".com",
    GIF = ".gif",
    TEX = ".tex",
    MD = ".md",
    CUBE = ".cube",
    SMILE = ".smi",
    MOL = ".mol",
    RXN = ".rxn",
    KET = ".ket",
    SMARTS = ".sma",
    CML = ".cml",
    SDF = ".sdf",
    RDF = ".rdf",
}

export enum PaneType {
    TEXT = "text",
    TEMP_FILE = "temp_file",
    PDF = "pdf",
    IMAGE = "image",
    JSMOL = "jsmol",
    CHAT_BOT = "chat_bot",
    Home = "home",
    AITOMOL = "aitomol",
    EXCEL = "excel",
    WORD = "word",
    KETCHER = "ketcher",
}

export enum PaneFunction {
    SUBMIT = "submit",
    SAVE = "save",
    GUI = "gui",
    PDFLATEX = "pdflatex",
    MD_RENDER = "md_render",
    CONVERT = "convert",
    KETCHER = "ketcher",
}

export type Pane = {
    label: string,
    path?: string,
    type: PaneType,
    area?: string,
    key: string,
    modified: boolean,
    paneFunctions?: PaneFunction[],
}

interface FileStore {
    fileTree?: FileInfoExtended | undefined;
    renamedKey?: string;
    baseHome?: string; // 当前活动根目录路径(兼容用途)
    roots: WorkspaceRoot[]; // 工作区根目录列表(home 恒为第一个)
    activeRootId?: string; // 当前活动根 ID
    includeHidden: boolean;
    onlyShowShared: boolean;
    sharedPathSet: Set<string>;
    panes: Pane[];
    activePanes: Pane[];
    activeFolderPath?: string;
    selectedKeys: string[];
    expandedKeys: string[];
    activeKeys: string[];
    aitomicShow: boolean;
    newlyCreatedFiles: Set<string>; // 新增：跟踪新创建的文件路径
    isShutDownManually: boolean; // 标记是否是手动关闭应用，区分意外断开和正常关闭
    isMarkdownRender: boolean;
    setAitomicShow: (show: boolean) => void;
    markFileAsNew: (filePath: string) => void; // 新增：标记文件为新创建
    markFileAsViewed: (filePath: string) => void; // 新增：标记文件已查看，移除新文件状态
    isFileNew: (filePath: string) => boolean; // 新增：检查文件是否为新创建
    doesFolderContainNewFiles: (folderPath: string) => boolean; // 新增：检查文件夹是否包含新文件

    setSelectedKeys: (paths: string[]) => void

    initializeTreedata: () => Promise<void>;
    loadRoots: () => Promise<void>;
    switchRoot: (id: string) => Promise<void>;
    openFolder: (path: string) => Promise<void>;
    openInJSMOL: (path: string) => Promise<void>;
    openInAitomol: (path:string) => Promise<void>;
    openInKetcher: (path?: string) => Promise<void>;
    onCloseTab: (key: string) => void;
    onCloseTabsByPath: (path: string) => void;
    onTabMove: (params: DragEndEvent) => void;
    closeFolder: (path: string) => void;
    updateFolder: (path: string) => Promise<void>;
    openFile: (path: string, type?: PaneType) => Promise<void>;
    openShareFile: (path: string) => Promise<void>;

    createTempFile: () => void;
    saveTempFile: (name: string, path: string, key: string) => Promise<boolean>

    deleteFile: (paths: string[]) => Promise<boolean>;
    renameFile: (path: string, newName: string) => Promise<boolean>;
    openFolderOrFile: (path: string) => Promise<void>;
    moveFile: (sourcePath: string[], targetPath: string) => Promise<boolean>;

    openChatBot: () => void;
    openHome: () => void;

    onFileChange: (path: string) => Promise<void>;
    onFileSave: (path: string, content?: string) => Promise<boolean>;
    onTabChange: (key: string) => void;
    // selectFile: (path: string) => void;
    // deselectFile: (path: string) => void;
    setExpandedKeys: (keys: string[]) => void;
    navigateToFile: (path: string) => void;

    setRenamedKey: (key?: string) => void;
    setMarkdownRender: (value: boolean) => void;
    setActiveFolderPath: (key?: string) => void;
    setIsShutDownManually: (value: boolean) => void;
    setIncludeHidden: (value: boolean) => Promise<void>;
    setOnlyShowShared: (value: boolean) => Promise<void>;

    getFileNode: (path: string) => Promise<FileInfoExtended | undefined>;
}

type LocalStorageData = {
    state?: {
        expandedKeys?: string[];
        includeHidden?: boolean;
    },
    version?: number
}

const sanitizePathList = (paths: unknown): string[] => {
    if (!Array.isArray(paths)) return [];
    return paths.filter((path): path is string => typeof path === 'string' && path.length > 0);
};


export const rootOfPath = (roots: WorkspaceRoot[] | undefined, path: string): WorkspaceRoot | undefined => {
    if (!roots || roots.length === 0 || !path || path === '/') return undefined;
    let best: WorkspaceRoot | undefined;
    for (const root of roots) {
        if (!root.path) continue;
        const rootPath = root.path.replace(/\/+$/, '');
        if (path === rootPath || path.startsWith(rootPath + '/')) {
            if (!best || rootPath.length > (best.path || '').replace(/\/+$/, '').length) {
                best = root;
            }
        }
    }
    return best;
};

export const isRootPath = (roots: WorkspaceRoot[] | undefined, path: string): boolean => {
    if (!roots || roots.length === 0 || !path) return false;
    const normalized = path.replace(/\/+$/, '');
    return roots.some(root => !!root.path && root.path.replace(/\/+$/, '') === normalized);
};

const isCurrentRoot = (roots: WorkspaceRoot[], baseHome: string | undefined, path: string): boolean => {
    if (!path) return false;
    if (roots.length > 0) return isRootPath(roots, path);
    return !!baseHome && baseHome === path;
};

export const findNode = (node: FileInfoExtended, targetPath: string): FileInfoExtended | undefined => {
    // 找到目标节点
    if (node.path === targetPath) {
        return node;
    }

    // 如果当前节点有子节点，则递归搜索
    if (node.children) {
        for (const child of node.children) {
            const result = findNode(child, targetPath);
            if (result) {
                return result;
            }
        }
    }

    return undefined;
};

const isSharedRelevant = (path: string, sharedSet: Set<string>): boolean => {
    for (const sharedPath of sharedSet) {
        if (sharedPath === path) return true;
        if (sharedPath.startsWith(path + '/')) return true;
        if (path.startsWith(sharedPath + '/')) return true;
    }
    return false;
};

const pruneSharedTree = (node: FileInfoExtended, sharedSet: Set<string>) => {
    if (!node.children) return;
    node.children = node.children.filter(child => isSharedRelevant(child.path, sharedSet));
    for (const child of node.children) {
        pruneSharedTree(child, sharedSet);
    }
};

const collectDirPaths = (node: FileInfoExtended): string[] => {
    const result: string[] = [];
    const walk = (current: FileInfoExtended) => {
        if (!current.children) return;
        for (const child of current.children) {
            if (child.isDir) {
                result.push(child.path);
            }
            walk(child);
        }
    };
    walk(node);
    return result;
};

const buildPathChain = (baseHome: string, targetPath: string): string[] => {
    if (!baseHome) return [];
    if (!targetPath.startsWith(baseHome)) return [baseHome];

    const relativePath = targetPath.slice(baseHome.length).replace(/^\/+/, '');
    if (!relativePath) return [baseHome];

    const segments = relativePath.split('/').filter(Boolean);
    const paths = [baseHome];
    let currentPath = baseHome;

    for (const segment of segments) {
        currentPath = pathbe.join(currentPath, segment);
        paths.push(currentPath);
    }

    return paths;
};

const buildRootPathChain = (roots: WorkspaceRoot[], targetPath: string): string[] => {
    const root = rootOfPath(roots, targetPath);
    if (!root?.path) return [];
    return buildPathChain(root.path, targetPath);
};

const getFolderIcon = (folderPath: string, isExpanded: boolean) => {
    return isExpanded
        ? <FolderOpen id={`folder-open-${folderPath}`} />
        : <FolderClose id={`folder-close-${folderPath}`} />;
};

const mergeTreeNode = (
    currentNode: FileInfoExtended,
    incomingNode: FileInfoExtended,
    loadedPathSet: Set<string>
) => {
    currentNode.name = incomingNode.name;
    currentNode.size = incomingNode.size;
    currentNode.extension = incomingNode.extension;
    currentNode.modified = incomingNode.modified;
    currentNode.mode = incomingNode.mode;
    currentNode.isDir = incomingNode.isDir;
    currentNode.isSymlink = incomingNode.isSymlink;
    currentNode.isIgnore = incomingNode.isIgnore;
    currentNode.type = incomingNode.type;
    currentNode.sorting = incomingNode.sorting;
    currentNode.numDirs = incomingNode.numDirs;
    currentNode.numFiles = incomingNode.numFiles;
    currentNode.subtitles = incomingNode.subtitles;
    currentNode.content = incomingNode.content;
    currentNode.checksums = incomingNode.checksums;
    currentNode.token = incomingNode.token;

    if (!incomingNode.children) {
        return;
    }

    const existingChildren = currentNode.children ?? [];
    const incomingChildren = incomingNode.children;
    const childMap = new Map(existingChildren.map(child => [child.path, child]));
    const mergedChildren: FileInfoExtended[] = [];

    for (const incomingChild of incomingChildren) {
        const existingChild = childMap.get(incomingChild.path);
        if (existingChild) {
            mergeTreeNode(existingChild, incomingChild, loadedPathSet);
            mergedChildren.push(existingChild);
        } else {
            mergedChildren.push(incomingChild);
        }
    }

    if (loadedPathSet.has(incomingNode.path)) {
        currentNode.children = sortChildren(mergedChildren);
        return;
    }

    const incomingPathSet = new Set(incomingChildren.map(child => child.path));
    const untouchedChildren = existingChildren.filter(child => !incomingPathSet.has(child.path));
    currentNode.children = sortChildren([...untouchedChildren, ...mergedChildren]);
};

const iconMapGenerator = (fileTree: FileInfoExtended, selectedPaths?: string[]) => {


    const traverse = (node: FileInfoExtended) => {
        if (node.children) {
            node.children.forEach((child) => {
                traverse(child);
            });
        }

        if (!node?.icon) {
            if (node.isDir) {
                if (selectedPaths?.includes(node.path)) {
                    node.icon = getFolderIcon(node.path, true);
                } else {
                    node.icon = getFolderIcon(node.path, false);
                }
            } else {
                if (node.type === FileType.PDF) {
                    node.icon = <FilePdfOutlined />
                } 
                else if(node.type === FileType.BLOB && isWordFile(node.path)){
                    node.icon = <FileWordOutlined />
                }
                else if(node.type === FileType.BLOB && isExcelFile(node.path)){
                    node.icon = <FileExcelOutlined />
                }
                else if(node.type === FileType.TEXT && isLogFile(node.path)){
                    node.icon = <FileTextOutlined />
                }
                else {
                    node.icon = <FileOutlined />;
                }
            }
        }
    };
    traverse(fileTree);
}

const getPaneFunction = (fileNode: FileInfoExtended): PaneFunction[] => {
    let paneFunction: PaneFunction[] = [];

    switch (fileNode.extension) {
        case FileExtension.INP:
        case FileExtension.PY:
        case FileExtension.COM:
            paneFunction.push(PaneFunction.SUBMIT);
            break;
        case FileExtension.GIF:
            if (fileNode.type === FileType.TEXT) {
                paneFunction.push(PaneFunction.SUBMIT);
            }
            break;
        case FileExtension.XYZ:
        case FileExtension.PDB:
        case FileExtension.PQR:
        case FileExtension.MOL2:
        case FileExtension.CUBE:
            paneFunction.push(PaneFunction.GUI, PaneFunction.KETCHER, PaneFunction.CONVERT);
            break;
        case FileExtension.SDF:
        case FileExtension.MOL:
        case FileExtension.RXN:
            paneFunction.push(PaneFunction.GUI, PaneFunction.KETCHER, PaneFunction.CONVERT);
            break;
        case FileExtension.SMILE:
        case FileExtension.KET:
        case FileExtension.SMARTS:
        case FileExtension.CML:
        case FileExtension.RDF:
            paneFunction.push(PaneFunction.KETCHER, PaneFunction.CONVERT);
            break;
        case FileExtension.LOG:
            if (fileNode.type === FileType.GAUSSIAN) {
                paneFunction.push(PaneFunction.GUI, PaneFunction.SAVE, PaneFunction.CONVERT);
            }
            break;
        case FileExtension.TEX:
            paneFunction.push(PaneFunction.PDFLATEX);
            break;

    }

    if (fileNode.type === FileType.TEXT) {
        paneFunction.push(PaneFunction.SAVE);
    }

    if (fileNode.extension === FileExtension.MD) {
        paneFunction.push(PaneFunction.MD_RENDER);
    }


    return paneFunction
}

const parseLocalStorage = (): LocalStorageData | undefined => {
    const data = localStorage.getItem('file-storage');
    if (data) {
        try {
            return JSON.parse(data);
        } catch (e) {
            console.error(`Error parsing localStorage key file-storage:`, e);
        }
    }
    return undefined;
}

const sortChildren = (children: FileInfoExtended[]) => {
    return children.sort((a, b) => {
        // If one is a dir and the other is not, the dir comes first
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;

        // If both are the same type (both dirs or both files), then sort by name
        const nameA = pathbe.basename(a.path);
        const nameB = pathbe.basename(b.path);

        // Check if both file names start with numbers
        const numA = /^\d+/.exec(nameA);
        const numB = /^\d+/.exec(nameB);

        // If both start with numbers, compare numerically
        if (numA && numB) {
            return parseInt(numA[0]) - parseInt(numB[0]);
        }
        // If only A starts with number, A comes first
        if (numA) return -1;
        // If only B starts with number, B comes first
        if (numB) return 1;

        // Sort uppercase before lowercase
        const firstCharA = nameA.charAt(0);
        const firstCharB = nameB.charAt(0);
        const isUpperA = /^[A-Z]/.test(firstCharA);
        const isUpperB = /^[A-Z]/.test(firstCharB);

        if (isUpperA && !isUpperB) return -1;
        if (!isUpperA && isUpperB) return 1;

        // Otherwise sort alphabetically
        return nameA.localeCompare(nameB);
    });
}


/**
 * TODO:
 * 1. move create file/folder into fileStore
 * 2. move upload file/folder into fileStore
 * 3. optimize refesh file tree by create&upload
 */
const useFileStore = create<FileStore>()(
    persist(immer((set, get) => ({
        fileTree: undefined,
        baseHome: undefined,
        roots: [],
        activeRootId: undefined,
        renamedKey: undefined,
        includeHidden: false,
        onlyShowShared: false,
        sharedPathSet: new Set<string>(),
        selectedKeys: [],
        expandedKeys: [],
        panes: [],
        activePanes: [],
        activeKeys: [],
        aitomicShow: true,
        isShutDownManually: false,
        newlyCreatedFiles: new Set<string>(), // 新增：初始化新文件集合
        isMarkdownRender: true,
        setAitomicShow: (show: boolean) => {
            set((state) => {
                state.aitomicShow = show;
            })
        },

        // 新增：标记文件为新创建
        markFileAsNew: (filePath: string) => {
            set((state) => {
                state.newlyCreatedFiles.add(filePath);
            })
        },

        // 新增：标记文件已查看，移除新文件状态
        markFileAsViewed: (filePath: string) => {
            set((state) => {
                state.newlyCreatedFiles.delete(filePath);
                console.log("file viewed:", filePath)
                console.log("state:", state.newlyCreatedFiles)
            })
        },

        // 新增：检查文件是否为新创建
        isFileNew: (filePath: string) => {
            return get().newlyCreatedFiles.has(filePath);
        },

        // 新增：检查文件夹是否包含新文件或新子文件夹
        doesFolderContainNewFiles: (folderPath: string) => {
            const newFiles = get().newlyCreatedFiles;
            for (const filePath of newFiles) {
                if (filePath.startsWith(folderPath + '/')) {
                    return true;
                }
            }
            return false;
        },
        loadRoots: async () => {
            const res = await fileApi.getRoots();
            if (!res?.ok || !res.data) {
                return;
            }
            const rootsRes = res.data.roots ?? [];
            const defaultRoot = rootsRes.find(r => r.id === res.data.defaultRootId) ?? rootsRes[0];
            const homeRoot = rootsRes.find(r => r.kind === 'home');
            // fileService.baseHome 保持 home，用于 .jupyter/.aitomia 等配置类监听
            if (homeRoot?.path) {
                fileService.baseHome = homeRoot.path;
            }
            set((state) => {
                state.roots = rootsRes;
                const keepsActive = !!state.activeRootId && rootsRes.some(r => r.id === state.activeRootId);
                state.activeRootId = keepsActive ? state.activeRootId : (defaultRoot?.id ?? state.activeRootId);
                if (rootsRes.length > 0) {
                    const active = rootsRes.find(r => r.id === state.activeRootId);
                    state.baseHome = active?.path ?? defaultRoot?.path ?? state.baseHome;
                }
            });
        },
        switchRoot: async (id: string) => {
            const roots = get().roots;
            const root = roots.find(r => r.id === id);
            if (!root || !root.path) return;
            if (id === get().activeRootId) return;
            set((state) => {
                state.activeRootId = id;
                state.baseHome = root.path;
                state.selectedKeys = [];
                state.activeFolderPath = undefined;
                state.expandedKeys = state.expandedKeys.filter(key => rootOfPath(roots, key)?.id === id);
            });
            await get().initializeTreedata();
        },
        initializeTreedata: async () => {
            const localData = parseLocalStorage();
            const persistedExpandedKeys = sanitizePathList(localData?.state?.expandedKeys);
            const currentExpandedKeys = get().expandedKeys;
            const expandedKeys = currentExpandedKeys.length > 0 ? currentExpandedKeys : persistedExpandedKeys;
            const includeHidden = get().includeHidden;
            const onlyShowShared = get().onlyShowShared;

            // Keep persisted expanded keys safe for antd Tree internals.
            if (expandedKeys.length !== get().expandedKeys.length) {
                set({ expandedKeys });
            }

            const userStateRes = await authApi.userIsReady();
            if (!userStateRes?.ok || !userStateRes.data?.isReady) {
                return;
            }

            let pathsToExpand = expandedKeys;
            // 确保根目录列表已加载
            if (get().roots.length === 0) {
                await get().loadRoots();
            }
            const rootsNow = get().roots;
            const activeRoot =
                rootsNow.find(r => r.id === get().activeRootId)
                ?? rootsNow.find(r => r.isDefault)
                ?? rootsNow[0];
            const activeRootPath = activeRoot?.path ?? undefined;
            // 多根模式下只展开活动根下的目录，避免把其他根的子树合并进当前树
            if (activeRootPath && !onlyShowShared) {
                pathsToExpand = pathsToExpand.filter(key => key === activeRootPath || key.startsWith(activeRootPath + '/'));
            }
            let sharedPathSet: Set<string> | undefined;

            if (onlyShowShared) {
                const shareRes = await shareApi.getShareWithMe();
                const sharedPaths = (shareRes?.ok && shareRes.data ? shareRes.data : [])
                    .filter(entry => entry.status !== 'pending' && !!entry.symbol_link_path)
                    .map(entry => entry.symbol_link_path as string);
                sharedPathSet = new Set(sharedPaths);
                pathsToExpand = sharedPaths;
            }

            const res = await fileApi.getFileResourceList({ paths: pathsToExpand, target: activeRootPath, includeHidden });
            if (!res?.ok) {
                console.log("get file resource list failed", res?.message);
                return
            };
            let fileTree = res.data as FileInfoExtended;

            if (onlyShowShared && sharedPathSet) {
                pruneSharedTree(fileTree, sharedPathSet);
            }

            const effectiveExpandedKeys = onlyShowShared ? collectDirPaths(fileTree) : expandedKeys;
            iconMapGenerator(fileTree, effectiveExpandedKeys);
            if (res.data?.baseHome) {
                // start file service
                const baseHome = res.data?.baseHome;
                // 不再调用fileService.initWebSocket，因为现在由统一的websocketService管理
                // fileService.baseHome 保持 home 目录，用于 .jupyter/.aitomia 等配置类监听
                const homeRootNow = get().roots.find(r => r.kind === 'home');
                fileService.baseHome = homeRootNow?.path ?? baseHome; // 设置baseHome(home)
                const watchPaths = [...effectiveExpandedKeys];
                if (activeRootPath && !watchPaths.includes(activeRootPath)) {
                    watchPaths.push(activeRootPath);
                }
                fileService.listenToPath(watchPaths)

                // set file event callback
                fileEventQueue.setEventCallback("fileStore", async (events) => {
                    // 检查 WebSocket 连接状态
                    if (!websocketService.isConnectedStatus()) {
                        console.warn('[fileStore] WebSocket not connected, events may be delayed');
                    }

                    for (const event of events) {
                        // get parent path for refresh
                        const pathParts = event.path.split('/');
                        pathParts.pop();
                        const parentPath = pathParts.join('/');

                        switch (event.type) {
                            case FileEventType.CREATE:
                                if (get().fileTree) {
                                    // 记录更新前的文件列表
                                    const parentNode = findNode(get().fileTree!, parentPath);
                                    const oldFileList = parentNode?.children?.map(child => child.path) || [];

                                    // 更新文件夹
                                    await get().updateFolder(parentPath);

                                    // 对比新旧文件列表，标记新文件和新文件夹
                                    const updatedParentNode = findNode(get().fileTree!, parentPath);
                                    if (updatedParentNode?.children) {
                                        const newFileList = updatedParentNode.children.map(child => child.path);
                                        const newItems = newFileList.filter(itemPath => !oldFileList.includes(itemPath));

                                        // 标记新文件和新文件夹
                                        newItems.forEach(itemPath => {
                                            if (!get().isFileNew(itemPath)) {
                                                // 添加检查，避免重复标记
                                                get().markFileAsNew(itemPath);
                                            }
                                        });
                                    }
                                }

                                break;
                            case FileEventType.DELETE:
                                console.log("delete event", event)
                                console.log("parent path", parentPath)
                                const filteredKeys = get().expandedKeys.filter(key => {
                                    console.log("filter:", key, pathbe.join(event.path, "/"))
                                    return !key.startsWith(pathbe.join(event.path, "/")) && key !== event.path
                                });
                                get().onCloseTabsByPath(event.path);
                                get().setExpandedKeys(filteredKeys)
                                if (get().fileTree) {
                                    const parentNode = findNode(get().fileTree!, parentPath);
                                    if (parentNode) {
                                        await get().updateFolder(parentPath);
                                    }
                                }
                                break;
                            case FileEventType.MOVE:
                                if (get().fileTree) {
                                    const parentNode = findNode(get().fileTree!, parentPath);
                                    if (parentNode) {
                                        await get().updateFolder(parentPath);
                                    }
                                }
                                break;
                        }
                    }
                });
            }
            set(state => {
                state.fileTree = fileTree;
                // baseHome 表示当前活动根目录路径，兼容旧组件默认目录语义
                state.baseHome = activeRootPath ?? res.data?.baseHome;
                if (activeRoot?.id) {
                    state.activeRootId = activeRoot.id;
                }
                if (onlyShowShared && sharedPathSet) {
                    state.sharedPathSet = sharedPathSet;
                    state.expandedKeys = effectiveExpandedKeys;
                }
            })
        },
        updateFolder: async (path: string) => {
            let loadingTimer: NodeJS.Timeout | null = null;
            loadingTimer = setTimeout(() => {
                set((state) => {
                    if (!state.fileTree) return;
                    const node = findNode(state.fileTree, path);
                    if (!node) return;
                    const info = { icon: <LoadingOutlined /> };
                    Object.assign(node, info);
                });
            }, 300);

            try {
                const paths = get().expandedKeys.filter(key => key.startsWith(pathbe.join(path, "/")))
                const res = await fileApi.getFileResourceList({ paths, target: path, includeHidden: get().includeHidden });

                if (!res?.ok) {
                    getAntdApp().message.error(res?.message || i18n.t('file_store.update_folder_failed', { ns: 'common' }));
                    return
                }
                let fileTree = res.data as FileInfoExtended;
                if (get().onlyShowShared) {
                    pruneSharedTree(fileTree, get().sharedPathSet);
                }
                // set paths to keep origin node's state
                iconMapGenerator(fileTree, paths);
                set((state) => {
                    if (!state.fileTree) return;
                    const info = {
                        children: fileTree.children,
                        icon: getFolderIcon(path, true),
                    };
                    const node = findNode(state.fileTree, path);
                    if (!node) return;
                    console.log("update folder", path, info)
                    Object.assign(node, info);
                });
            } catch (e) {
                console.log(e)
            } finally {
                if (loadingTimer) {
                    clearTimeout(loadingTimer);
                    loadingTimer = null;
                }

                set((state) => {
                    if (!state.fileTree) return;
                    const node = findNode(state.fileTree, path);
                    if (!node?.isDir) return;
                    const isExpanded = state.expandedKeys.includes(path) || isCurrentRoot(get().roots, state.baseHome, path);
                    node.icon = getFolderIcon(path, isExpanded);
                });
            }
        },
        openFolder: async (path: string) => {
            const roots = get().roots;
            const currentRootPath = rootOfPath(roots, path)?.path ?? get().baseHome;
            if (currentRootPath && path !== currentRootPath) {
                const parentPath = pathbe.dirname(path);
                const parentRes = await fileApi.getFileResource(parentPath, get().includeHidden);
                if (!parentRes?.ok) {
                    getAntdApp().message.error(parentRes?.message || i18n.t('file_store.open_folder_failed', { ns: 'common' }));
                    return;
                }
                const parentNode = parentRes.data as FileInfoExtended | undefined;
                const exists = parentNode?.children?.some((item) => item.path === path);
                if (!exists) {
                    getAntdApp().message.error(i18n.t('file_store.not_exist', { ns: 'common' }));
                    return;
                }
            }

            let loadingTimer: NodeJS.Timeout | null = null;

            loadingTimer = setTimeout(() => {
                set((state) => {
                    if (!state.fileTree) return;
                    const node = findNode(state.fileTree, path);
                    if (!node) return;
                    const info = { icon: <LoadingOutlined /> };
                    Object.assign(node, info);
                });
            }, 300);

            try {
                const res = await fileApi.getFileResource(path, get().includeHidden);

                if (!res?.ok) {
                    getAntdApp().message.error(res?.message || i18n.t('file_store.open_folder_failed', { ns: 'common' }));
                    return
                }
                let fileTree = res.data as FileInfoExtended;
                if (get().onlyShowShared) {
                    pruneSharedTree(fileTree, get().sharedPathSet);
                }
                iconMapGenerator(fileTree, []);
                set((state) => {
                    if (!state.fileTree) return;
                    const info = { icon: getFolderIcon(path, true), children: fileTree.children };
                    const node = findNode(state.fileTree, path);
                    if (!node) return;
                    Object.assign(node, info);
                    const expandedKeys = [...new Set([...state.expandedKeys, path])]
                    state.expandedKeys = expandedKeys;
                    fileService.listenToPath(expandedKeys)
                });

                // 当用户打开文件夹时，将其标记为已查看，移除新文件夹状态
                get().markFileAsViewed(path);
            } catch (e) {
                console.log(e)
            } finally {
                if (loadingTimer) {
                    clearTimeout(loadingTimer);
                    loadingTimer = null;
                }

                set((state) => {
                    if (!state.fileTree) return;
                    const node = findNode(state.fileTree, path);
                    if (!node?.isDir) return;
                    const isExpanded = state.expandedKeys.includes(path) || isCurrentRoot(get().roots, state.baseHome, path);
                    node.icon = getFolderIcon(path, isExpanded);
                });
            }


        },
        closeFolder: (path: string) => {
            set((state) => {
                if (!state.fileTree) return;
                const info = { icon: getFolderIcon(path, false) };
                const node = findNode(state.fileTree, path);
                if (!node) return;
                Object.assign(node, info);
                // filter directory and its children
                const expandedKeys = state.expandedKeys.filter(key => !key.startsWith(pathbe.join(path, "/")) && key !== path);
                const listenedKeys = fileService.pathListened.filter(key => {
                    return !key.startsWith(pathbe.join(path, "/")) && key !== path
                })
                state.expandedKeys = expandedKeys;
                fileService.listenToPath(listenedKeys);

                // 关闭文件夹时，清理该文件夹下的新文件标记
                const filesToRemove = new Set<string>();
                for (const filePath of state.newlyCreatedFiles) {
                    if (filePath.startsWith(path + '/')) {
                        filesToRemove.add(filePath);
                    }
                }
                filesToRemove.forEach(filePath => {
                    state.newlyCreatedFiles.delete(filePath);
                });
            })
        },
        openFile: async (path: string) => {
            const roots = get().roots;
            const currentRootPath = rootOfPath(roots, path)?.path ?? get().baseHome;
            if (currentRootPath) {
                const parentPath = pathbe.dirname(path);
                const parentRes = await fileApi.getFileResource(parentPath, get().includeHidden);
                if (!parentRes?.ok) {
                    getAntdApp().message.error(parentRes?.message || i18n.t('file_store.open_file_failed', { ns: 'common' }));
                    return;
                }
                const parentNode = parentRes.data as FileInfoExtended | undefined;
                const exists = parentNode?.children?.some((item) => item.path === path);
                if (!exists) {
                    getAntdApp().message.error(i18n.t('file_store.not_exist', { ns: 'common' }));
                    return;
                }
            }

            get().markFileAsViewed(path);
            set(
                (state) => {
                    if (!state.fileTree) return;

                    // check which function is available for the file
                    const fileNode = findNode(state.fileTree, path);
                    if (!fileNode) return;
                    let type: PaneType | undefined;

                    if (fileNode.type === FileType.PDF) {
                        type = PaneType.PDF;
                    } else if (fileNode.type === FileType.IMAGE) {
                        type = PaneType.IMAGE;
                    } else if (/\.(xlsx|xls|xlsm)$/i.test(path)) {
                        type = PaneType.EXCEL;
                    } else if (/\.(docx|doc)$/i.test(path)) {
                        type = PaneType.WORD;
                    } else if (fileNode.type === FileType.GAUSSIAN || fileNode.type === FileType.TEXT) {
                        type = PaneType.TEXT;
                    } else {
                        type = PaneType.TEXT;
                    }


                    // check if the pane already exists
                    const reduplicatedPane = state.panes.find(pane => pane.path === path && pane.type === type);
                    if (!!reduplicatedPane) {
                        state.activePanes = [reduplicatedPane];
                        return
                    };


                    const paneFunction = getPaneFunction(fileNode);

                    const pathlist = new Set([...fileService.pathListened, path])
                    fileService.listenToPath([...pathlist])

                    const newPane: Pane = {
                        label: pathbe.basename(path),
                        path,
                        type,
                        key: `${path}_${type}`,
                        modified: false,
                        paneFunctions: paneFunction,
                    }
                    state.panes.push(newPane)
                    state.activePanes = [newPane];
                }
            )
            get().setSelectedKeys([path]);
        },
        openShareFile: async (path: string) => {
            // Shared files live outside the user's baseHome and are not in the local fileTree.
            // Fetch file metadata directly from the API.
            const fileRes = await fileApi.getFileResource(path, get().includeHidden);
            if (!fileRes?.ok || !fileRes.data) {
                getAntdApp().message.error(i18n.t('file_store.open_file_failed', { ns: 'common' }));
                return;
            }
            const fileNode = fileRes.data as FileInfoExtended;

            let type: PaneType;
            if (fileNode.type === FileType.PDF) {
                type = PaneType.PDF;
            } else if (fileNode.type === FileType.IMAGE) {
                type = PaneType.IMAGE;
            } else if (/\.(xlsx|xls|xlsm)$/i.test(path)) {
                type = PaneType.EXCEL;
            } else if (/\.(docx|doc)$/i.test(path)) {
                type = PaneType.WORD;
            } else {
                type = PaneType.TEXT;
            }

            // Reuse an already-open pane for the same path/type
            const existingPane = get().panes.find(pane => pane.path === path && pane.type === type);
            if (existingPane) {
                set(state => { state.activePanes = [existingPane]; });
                return;
            }

            const paneFunction = getPaneFunction(fileNode);

            if (type === PaneType.TEXT) {
                const pathlist = new Set([...fileService.pathListened, path]);
                fileService.listenToPath([...pathlist]);
            }

            const newPane: Pane = {
                label: pathbe.basename(path),
                path,
                type,
                key: `${path}_${type}`,
                modified: false,
                paneFunctions: paneFunction,
            };
            set(state => {
                state.panes.push(newPane);
                state.activePanes = [newPane];
            });
        },
        openFolderOrFile: async (path: string) => {
            const roots = get().roots;
            const activeRoot = roots.find(r => r.id === get().activeRootId);
            const userHome = activeRoot?.path ?? get().baseHome;
            if (!userHome) return;

            const targetParentPath = path === userHome ? userHome : pathbe.dirname(path);
            const paths = roots.length > 0 ? buildRootPathChain(roots, targetParentPath) : buildPathChain(userHome, targetParentPath);
            if (paths.length === 0) {
                getAntdApp().message.error(i18n.t('file_store.not_exist', { ns: 'common' }));
                return;
            }

            const res = await fileApi.getFileResourceList({ paths, includeHidden: get().includeHidden });
            if (!res?.ok || !res.data) {
                getAntdApp().message.error(i18n.t('file_store.not_exist', { ns: 'common' }));
                return;
            }

            const fetchedTree = res.data as FileInfoExtended;
            iconMapGenerator(fetchedTree, get().expandedKeys);
            const targetNode = findNode(fetchedTree, path);
            if (!targetNode) {
                getAntdApp().message.error(i18n.t('file_store.not_exist', { ns: 'common' }));
                return;
            }

            const loadedPathSet = new Set(paths);
            set((state) => {
                if (!state.fileTree) {
                    state.fileTree = fetchedTree;
                } else {
                    mergeTreeNode(state.fileTree, fetchedTree, loadedPathSet);
                }

                const parentPath = targetNode.path === userHome ? userHome : pathbe.dirname(targetNode.path);
                const parentChain = roots.length > 0 ? buildRootPathChain(roots, parentPath) : buildPathChain(userHome, parentPath);
                state.expandedKeys = [...new Set([...state.expandedKeys, ...parentChain])];
            });

            fileService.listenToPath(get().expandedKeys);
            console.log("target node:", targetNode)
            if (targetNode.isDir) {
                await get().openFolder(path);
            } else {
                await get().openFile(path);
            }
        },
        openInJSMOL: async (path: string) => {
            set(
                (state) => {
                    if (!state.fileTree) return;
                    let type: PaneType = PaneType.JSMOL;
                    // check if the pane already exists
                    const reduplicatedPane = state.panes.find(pane => pane.path === path && pane.type === type);
                    if (!!reduplicatedPane) {
                        state.activePanes = [reduplicatedPane];
                        return
                    };

                    const newPane: Pane = {
                        label: pathbe.basename(path),
                        path,
                        type,
                        key: `${path}_${type}`,
                        modified: false,
                    }
                    state.panes.push(newPane)
                    state.activePanes = [newPane];
                }
            )
        },
        openInAitomol: async (path: string) => {
            set(
                (state) => {
                    if (!state.fileTree) return;
                    let type: PaneType = PaneType.AITOMOL;
                    // check if the pane already exists
                    const reduplicatedPane = state.panes.find(pane => pane.path === path && pane.type === type);
                    if (!!reduplicatedPane) {
                        state.activePanes = [reduplicatedPane];
                        return
                    };

                    const newPane: Pane = {
                        label: pathbe.basename(path),
                        path,
                        type,
                        key: `${path}_${type}`,
                        modified: false,
                    }
                    state.panes.push(newPane)
                    state.activePanes = [newPane];
                }
            )
        },
        openInKetcher: async (path?: string) => {
            set(
                (state) => {
                    if (!state.fileTree) return;
                    let type: PaneType = PaneType.KETCHER;
                    if (!path) {
                        const newPane: Pane = {
                            label: "Molecule Editor",
                            type,
                            key: `untitled_${Date.now()}`,
                            modified: false,
                        }
                        state.panes.push(newPane)
                        state.activePanes = [newPane];
                        return;
                    }
                    const reduplicatedPane = state.panes.find(pane => pane.path === path && pane.type === type);
                    if (!!reduplicatedPane) {
                        state.activePanes = [reduplicatedPane];
                        return
                    };

                    const newPane: Pane = {
                        label: pathbe.basename(path),
                        path,
                        type,
                        key: `${path}_${type}`,
                        modified: false,
                    }
                    state.panes.push(newPane)
                    state.activePanes = [newPane];
                }
            )
        },
        openHome: () => {
            set((state) => {
                const reduplicatedPane = state.panes.find(pane => pane.type === PaneType.Home);
                if (!!reduplicatedPane) {
                    state.activePanes = [reduplicatedPane];
                    return
                };
                const newPane: Pane = {
                    label: "home",
                    type: PaneType.Home,
                    key: "home",
                    modified: false,
                }
                state.panes.push(newPane)
                state.activePanes = [newPane];
            })
        },
        openChatBot: () => {
            set((state) => {
                const reduplicatedPane = state.panes.find(pane => pane.type === PaneType.CHAT_BOT);
                if (!!reduplicatedPane) {
                    state.activePanes = [reduplicatedPane];
                    return
                };
                const newPane: Pane = {
                    label: "chatbot",
                    type: PaneType.CHAT_BOT,
                    key: `chatbot`,
                    modified: false,
                }
                state.panes.push(newPane)
                state.activePanes = [newPane];
            })
        },
        onFileChange: async (key: string) => {
            set((state) => {
                const pane = state.panes.find(pane => pane.key === key);
                if (pane) {
                    pane.modified = true;
                    state.activePanes = [pane];
                }
            })
        },
        onTabChange: (key: string) => {
            set((state) => {
                const pane = state.panes.find(pane => pane.key === key);
                if (pane) {
                    state.activePanes = [pane];
                }
                if (pane?.path) {
                    get().navigateToFile(pane.path);
                }
            })
        },
        onTabMove: ({ active, over }: DragEndEvent) => {
            set((state) => {
                if (!state.panes) return;
                if (active.id === over?.id) return;
                const activeIndex = state.panes.findIndex((i) => i.key === active.id);
                const overIndex = state.panes.findIndex((i) => i.key === over?.id);
                state.panes = arrayMove([...state.panes], activeIndex, overIndex);
            })
        },
        onCloseTab: (key: string) => {
            set((state) => {
                const panes = state.panes.filter(pane => pane.key !== key);

                const activePanes = state.activePanes.filter(pane => pane.key !== key);
                if (activePanes.length === 0 && panes.length > 0) {
                    const lastIndex = panes.length - 1;
                    activePanes.push(panes[lastIndex]);
                }
                state.panes = panes;
                state.activePanes = activePanes;
            })
            editorDb?.deleteFileData(key);
        },
        onCloseTabsByPath: (path: string) => {
            set((state) => {
                //!pane.path.startsWith(pathbe.join(path, "/")) means close children file's tabs
                const panes = state.panes.filter(pane => pane.path !== path && !pane?.path?.startsWith(pathbe.join(path, "/")));
                const activePanes = state.activePanes.filter(pane => pane.path !== path && !pane?.path?.startsWith(pathbe.join(path, "/")));
                if (activePanes.length === 0 && panes.length > 0) {
                    const lastIndex = panes.length - 1;
                    activePanes.push(panes[lastIndex]);
                }
                state.panes = panes;
                state.activePanes = activePanes;
            })
        },
        setSelectedKeys: (paths) => {
            set((state) => {
                state.selectedKeys = paths;
            })
        },
        onFileSave: async (key: string) => {
            const editorRef = FileEditorMapService.getInstance().getEditorRef(key);

            if (!editorRef) {
                getAntdApp().message.error(i18n.t('file_store.editor_not_found', { ns: 'common' }))
                return false
            }

            const targetPane = get().panes.find(pane => pane.key === key);
            if (!targetPane) {
                getAntdApp().message.error(i18n.t('file_store.pane_not_found', { ns: 'common' }))
                return false
            }

            if (!targetPane?.path) {
                console.log("temp file, no need to save")
                return false
            }

            const content = await editorRef.getContent()

            try {
                const res = await fileService.saveFile(targetPane?.path, content ?? "")

                if (!res?.ok) {
                    getAntdApp().message.error(res?.message ?? i18n.t('file_store.save_failed', { ns: 'common' }))
                    return false
                }

                set((state) => {
                    const pane = state.panes.find(pane => pane.key === key);
                    if (pane) {
                        pane.modified = false;
                        // Keep active pane synchronized with the same pane reference.
                        const activePane = state.activePanes.find(item => item.key === key);
                        if (activePane) {
                            state.activePanes = [pane];
                        }
                    }
                })
                return true
            } catch (e) {
                getAntdApp().message.error(i18n.t('file_store.save_failed', { ns: 'common' }))
                console.log(e)
                return false
            }


        },
        deleteFile: async (paths: string[]) => {
            try {
                const res = await fileService.deleteFile(paths);
                if (!res?.ok) {
                    getAntdApp().message.error(res?.message ?? i18n.t('file_store.delete_failed', { ns: 'common' }))
                    return false
                }

                set((state) => {
                    const panes = state.panes.filter(pane => !paths.includes(pane.path!) || !pane.path);
                    const activePanes = state.activePanes.filter(pane => !paths.includes(pane.path!) || !pane.path);
                    if (activePanes.length === 0 && panes.length > 0) {
                        const lastIndex = panes.length - 1;
                        activePanes.push(panes[lastIndex]);
                    }
                    state.panes = panes;
                    state.activePanes = activePanes;

                    // Update parent folder in the file tree
                    const parentPaths = new Set<string>();
                    paths.forEach(path => {
                        parentPaths.add(pathbe.dirname(path));
                    });

                    // // For each parent path, remove the deleted children from the file tree
                    // parentPaths.forEach(parentPath => {

                    //     if (state.expandedKeys.includes(parentPath) || state.baseHome === parentPath) {
                    //         const parentNode = findNode(state.fileTree!, parentPath);
                    //         if (parentNode && parentNode.children) {
                    //             parentNode.children = parentNode.children.filter(
                    //                 child => !paths.includes(child.path)
                    //             );
                    //         }
                    //     }
                    // });
                })

                // 检查 WebSocket 连接状态，如果未连接则手动刷新受影响的文件夹
                if (!websocketService.isConnectedStatus()) {
                    console.warn('[fileStore] WebSocket not connected, manually refreshing folders after delete');
                    const parentPaths = new Set<string>();
                    paths.forEach(path => {
                        parentPaths.add(pathbe.dirname(path));
                    });
                    for (const parentPath of parentPaths) {
                        if (get().expandedKeys.includes(parentPath) || isCurrentRoot(get().roots, get().baseHome, parentPath)) {
                            await get().updateFolder(parentPath);
                        }
                    }
                }

                return true
            } catch (e) {
                return false
            }
        },
        renameFile: async (path: string, newName: string) => {
            try {
                const res = await fileService.renameFile(path, newName);
                if (!res?.ok) {
                    getAntdApp().message.error(res?.message ?? i18n.t('file_store.rename_failed', { ns: 'common' }))
                    return false
                }

                set((state) => {
                    const panes = state.panes.map(pane => {
                        if (pane.path === path) {
                            pane.path = pathbe.join(pathbe.dirname(path), newName);
                            pane.label = newName;
                        }
                        return pane;
                    });
                    const activePanes = state.activePanes.map(pane => {
                        if (pane.path === path) {
                            pane.path = pathbe.join(pathbe.dirname(path), newName);
                            pane.label = newName;
                        }
                        return pane;
                    })
                    state.activePanes = activePanes;
                    state.panes = panes;

                    // Update the parent folder in the file tree
                    const parentPath = pathbe.dirname(path);

                    if (state.expandedKeys.includes(parentPath) || isCurrentRoot(get().roots, state.baseHome, parentPath)) {
                        const parentNode = findNode(state.fileTree!, parentPath);
                        if (parentNode && parentNode.children) {
                            const fileNode = parentNode.children.find(child => child.path === path);
                            if (fileNode) {
                                const newPath = pathbe.join(parentPath, newName);
                                fileNode.path = newPath;
                                fileNode.key = newPath;
                                fileNode.name = newName;

                                // Update keys if this renamed path was in expanded keys
                                state.expandedKeys = state.expandedKeys.map(key => {
                                    if (key === path) {
                                        return newPath;
                                    }
                                    // Update paths of children if they were expanded
                                    if (key.startsWith(path + '/')) {
                                        return newPath + key.substring(path.length);
                                    }
                                    return key;
                                });

                                // Sort children to maintain order
                                parentNode.children = sortChildren(parentNode.children);
                            }
                        }
                    }
                })

                // 检查 WebSocket 连接状态，如果未连接则手动刷新父文件夹
                if (!websocketService.isConnectedStatus()) {
                    console.warn('[fileStore] WebSocket not connected, manually refreshing folder after rename');
                    const parentPath = pathbe.dirname(path);
                    if (get().expandedKeys.includes(parentPath) || isCurrentRoot(get().roots, get().baseHome, parentPath)) {
                        await get().updateFolder(parentPath);
                    }
                }

                return true
            } catch (e) {
                getAntdApp().message.error(i18n.t('file_store.rename_failed', { ns: 'common' }))
                console.log(e)
                return false
            }
        },
        moveFile: async (sourceList: string[], targetPath: string) => {
            try {
                // Call API to move file
                const res = await fileService.moveFile(sourceList, targetPath);
                if (!res?.ok) {
                    getAntdApp().message.error(res?.message ?? i18n.t('file_store.move_failed', { ns: 'common' }));
                    return false;
                }

                const data = res.data;

                if (!data || data.length === 0) {
                    return true;
                }

                set((state) => {
                    // Update panes with the new path
                    const panes = state.panes.map(pane => {
                        for (const item of data) {
                            if (pane.path === item.src) {
                                pane.path = item.dst;
                                // Update key to reflect new path
                            }
                        }
                        return pane;
                    });

                    // Update active panes the same way
                    const activePanes = state.activePanes.map(pane => {
                        for (const item of data) {
                            if (pane.path === item.src) {
                                pane.path = item.dst;
                            }
                        }

                        return pane;
                    });

                    state.activePanes = activePanes;
                    state.panes = panes;

                    // Update the file tree
                    // Remove from source folder
                    for (const item of data) {
                        // let sourceParentPath = item.src;
                        // if(!item.isDir){
                        //     sourceParentPath = pathbe.dirname(item.src);
                        // }
                        // if (state.expandedKeys.includes(sourceParentPath) || state.baseHome === sourceParentPath) {
                        //     const sourceParentNode = findNode(state.fileTree!, sourceParentPath);
                        //     if (sourceParentNode && sourceParentNode.children) {
                        //         const sourceNode = sourceParentNode.children.find(child => child.path === item.src);
                        //         sourceParentNode.children = sourceParentNode.children.filter(
                        //             child => child.path !== item.src
                        //         );

                        //         // // Add to target folder
                        //         // if (state.expandedKeys.includes(targetPath) || state.baseHome === targetPath) {
                        //         //     const targetParentNode = findNode(state.fileTree!, targetPath);
                        //         //     if (targetParentNode && targetParentNode.children && sourceNode) {
                        //         //         // Update node path
                        //         //         sourceNode.path = targetPath;
                        //         //         sourceNode.key = targetPath;
                        //         //         targetParentNode.children.push(sourceNode);
                        //         //         // Sort children
                        //         //         targetParentNode.children = sortChildren(targetParentNode.children);
                        //         //     }
                        //         // }
                        //     }
                        // }

                        // Update expanded keys if necessary
                        state.expandedKeys = state.expandedKeys.map(key => {
                            if (key === item.src) {
                                return item.dst;
                            }
                            // Update paths of children if they were expanded
                            // if (key.startsWith(item.src + '/')) {
                            //     return targetPath + key.substring(sourcePath.length);
                            // }
                            return key;
                        });
                    }

                });
                console.log("panes", get().panes)

                // 检查 WebSocket 连接状态，如果未连接则手动刷新源文件夹和目标文件夹
                if (!websocketService.isConnectedStatus()) {
                    console.warn('[fileStore] WebSocket not connected, manually refreshing folders after move');
                    const affectedPaths = new Set<string>();

                    // 添加所有源文件的父目录
                    for (const item of data) {
                        const sourceParent = pathbe.dirname(item.src);
                        affectedPaths.add(sourceParent);
                    }

                    // 添加目标目录
                    affectedPaths.add(targetPath);

                    // 刷新所有受影响的文件夹
                    for (const affectedPath of affectedPaths) {
                        if (get().expandedKeys.includes(affectedPath) || isCurrentRoot(get().roots, get().baseHome, affectedPath)) {
                            await get().updateFolder(affectedPath);
                        }
                    }
                }

                return true;
            } catch (e) {
                getAntdApp().message.error(i18n.t('file_store.move_failed', { ns: 'common' }));
                console.log(e);
                return false;
            }
        },
        createTempFile: async () => {
            set((state) => {
                const key = uuidv4();
                const paneFunction = [PaneFunction.SAVE]
                const newPane: Pane = {
                    label: "untitled",
                    type: PaneType.TEMP_FILE,
                    key: `untitled_${key}`,
                    modified: false,
                    paneFunctions: paneFunction,
                }
                state.panes.push(newPane)
                state.activePanes = [newPane];
            })
        },
        saveTempFile: async (name: string, path: string, key: string) => {
            if (!validateLegalString(name).isValid) {
                getAntdApp().message.error(i18n.t('file_store.invalid_name', { ns: 'common' }));
                return false;
            }
            const editorRef = FileEditorMapService.getInstance().getEditorRef(key);
            if (!editorRef) {
                getAntdApp().message.error(i18n.t('file_store.editor_not_found', { ns: 'common' }))
                return false
            }
            const content = await editorRef.getContent()
            try {
                const res = await fileService.createFile(path, name, content ?? "")

                if (!res?.ok) {

                    getAntdApp().message.error(res?.message ?? i18n.t('file_store.save_failed', { ns: 'common' }))
                    return false
                }
                if (res.data?.hasConflicts) {
                    getAntdApp().message.error(i18n.t('file_store.file_exists', { ns: 'common' }))
                    return false
                }

                const newFileNode = res.data?.successFiles[0] as FileInfoExtended;
                newFileNode.icon = <FileOutlined />;

                // Update the temp pane to become a regular file pane
                set((state) => {
                    const pane = state.panes.find(pane => pane.key === key);

                    if (pane) {
                        const paneFunction = getPaneFunction(newFileNode);
                        pane.label = name;
                        pane.key = `${pathbe.join(path, name)}_${PaneType.TEXT}`;
                        pane.type = PaneType.TEXT;
                        pane.path = pathbe.join(path, name);
                        pane.modified = false;
                        pane.paneFunctions = paneFunction;

                        // Make sure the active pane references the updated pane
                        state.activePanes = [pane];
                    }
                });

                // Update the parent folder in the UI if it's not already expanded
                if (get().expandedKeys.includes(path) || isCurrentRoot(get().roots, get().baseHome, path)) {
                    set(state => {
                        const parentNode = findNode(state.fileTree!, path);
                        if (parentNode && parentNode.children) {
                            parentNode.children.push(newFileNode);
                            // Sort children: numbers first, then letters
                            parentNode.children = sortChildren(parentNode.children);
                        }
                    });
                }

                // 标记新创建的文件
                const newFilePath = pathbe.join(path, name);
                console.log("new file path:", newFilePath)
                get().markFileAsNew(newFilePath);

                // 检查 WebSocket 连接状态，如果未连接则手动刷新父文件夹
                if (!websocketService.isConnectedStatus()) {
                    console.warn('[fileStore] WebSocket not connected, manually refreshing folder after file creation');
                    if (get().expandedKeys.includes(path) || isCurrentRoot(get().roots, get().baseHome, path)) {
                        await get().updateFolder(path);
                    }
                }

                return true
            } catch (e) {
                getAntdApp().message.error(i18n.t('file_store.save_failed', { ns: 'common' }))
                console.log(e)
                return false
            }
        },
        setExpandedKeys: (keys) => set({ expandedKeys: sanitizePathList(keys) }),
        navigateToFile: (path: string) => {
            if (typeof path !== 'string' || path.length === 0) {
                console.warn('[fileStore] navigateToFile received an invalid path:', path);
                return;
            }

            // Helper function to ensure directories are loaded
            const ensureDirectoriesLoaded = async (targetPath: string) => {
                // Break the path into segments to build paths for each directory level
                const rootsNow = get().roots;
                const root = rootOfPath(rootsNow, targetPath);
                const baseHome = (root?.path ?? get().baseHome) || '';
                if (!baseHome || !targetPath.startsWith(baseHome)) return;

                // Get relative path from baseHome
                const relativePath = targetPath.substring(baseHome.length);
                if (!relativePath) return; // If target is the baseHome itself

                const segments = relativePath.split('/').filter(Boolean);
                let currentPath = baseHome;

                // Traverse through each directory level
                for (const segment of segments) {
                    currentPath = pathbe.join(currentPath, segment);

                    // Skip the last segment if it's not a directory (it's the file we want to open)
                    if (currentPath === targetPath && segments.length > 0) {
                        const fileTree = get().fileTree;
                        if (fileTree) {
                            const node = findNode(fileTree, pathbe.dirname(targetPath));
                            // If parent directory is loaded but doesn't contain this file
                            if (node && node.children && !node.children.some(child => child.path === targetPath)) {
                                // Update the parent folder to ensure the file is loaded
                                await get().updateFolder(pathbe.dirname(targetPath));
                            }
                        }
                        break;
                    }

                    if (!get().expandedKeys.includes(currentPath)) {
                        set((state) => {
                            // Add the current directory to expanded keys
                            state.expandedKeys = [...new Set([...state.expandedKeys, currentPath])];
                        });
                    }

                    // Check if this directory level is already loaded
                    const fileTree = get().fileTree;
                    if (fileTree) {
                        const node = findNode(fileTree, currentPath);
                        if (!node) {
                            // If the directory doesn't exist in our tree, check its parent
                            const parentNode = findNode(fileTree, pathbe.dirname(currentPath));
                            if (parentNode) {
                                // Update the parent to load this directory
                                await get().updateFolder(pathbe.dirname(currentPath));
                            }
                        } else if (!node.children && node.isDir) {
                            // If directory exists but children aren't loaded yet
                            await get().openFolder(currentPath);
                        }
                    }
                }
            };

            (async () => {
                await ensureDirectoriesLoaded(path);

                // After loading directories, find the node and open it
                const fileTree = get().fileTree;
                if (!fileTree) return;

                const fileNode = findNode(fileTree, path);
                if (!fileNode) {
                    // If still not found, try to update the parent directory
                    await get().updateFolder(pathbe.dirname(path));
                    const updatedTree = get().fileTree;
                    const updatedNode = updatedTree ? findNode(updatedTree, path) : undefined;

                    if (updatedNode) {
                        if (updatedNode.isDir) {
                            await get().openFolder(path);
                        } else {
                            if (!get().panes.find(pane => pane.path === path)) {
                                await get().openFile(path);
                            }
                        }
                    }
                } else {
                    // Node found, open it appropriately
                    if (fileNode.isDir) {
                        await get().openFolder(path);
                    } else {
                        if (!get().panes.find(pane => pane.path === path)) {
                            await get().openFile(path);
                        }
                    }
                }

                setTimeout(() => {
                    set(state => {
                        state.selectedKeys = [path];
                    });
                }, 100)
            })()
        },
        setRenamedKey: (key?: string) => {
            set({ renamedKey: key });
        },
        setMarkdownRender: (value: boolean) => {
            set({ isMarkdownRender: value });
        },
        setActiveFolderPath: (key?: string) => {
            set({ activeFolderPath: key });
        },
        setIsShutDownManually: (value: boolean) => {
            set({ isShutDownManually: value });
        },
        setIncludeHidden: async (value: boolean) => {
            if (get().includeHidden === value) {
                return;
            }
            set({ includeHidden: value });
            await get().initializeTreedata();
        },
        setOnlyShowShared: async (value: boolean) => {
            if (get().onlyShowShared === value) {
                return;
            }
            set({ onlyShowShared: value });
            await get().initializeTreedata();
        },
        getFileNode: async (path: string) => {
            const fileTree = get().fileTree;
            if (!fileTree) return undefined;

            const currentNode = findNode(fileTree, path);
            if (currentNode) return currentNode;

            const roots = get().roots;
            const activeRoot = roots.find(r => r.id === get().activeRootId);
            const userHome = activeRoot?.path ?? get().baseHome;
            if (!userHome) return undefined;

            const targetPath = path === userHome ? userHome : pathbe.dirname(path);
            const paths = roots.length > 0 ? buildRootPathChain(roots, targetPath) : buildPathChain(userHome, targetPath);
            if (paths.length === 0) return undefined;

            const res = await fileApi.getFileResourceList({ paths, includeHidden: get().includeHidden });
            if (!res?.ok || !res.data) return undefined;

            const fetchedTree = res.data as FileInfoExtended;
            iconMapGenerator(fetchedTree, get().expandedKeys);
            const loadedPathSet = new Set(paths);

            set((state) => {
                if (!state.fileTree) {
                    state.fileTree = fetchedTree;
                    return;
                }
                mergeTreeNode(state.fileTree, fetchedTree, loadedPathSet);
            });

            const updatedTree = get().fileTree;
            if (!updatedTree) return undefined;
            return findNode(updatedTree, path);
        }
    })), {
        name: 'file-storage', // name of the item in the storage (must be unique)
        storage: createJSONStorage(() => localStorage), // (optional) by default the 'localStorage' is used
        partialize: (state) => ({
            expandedKeys: state.expandedKeys,
            includeHidden: state.includeHidden,
            onlyShowShared: state.onlyShowShared,
            activePanes: state.activePanes,
            panes: state.panes,
            // isMarkdownRender: state.isMarkdownRender,
        }),
    })
);

export default useFileStore;